import React from 'react';
import MemberCell from './MemberCell';
import { getGenerationLabel, getLeftStripColor, getRightStripColor } from '../../utils/treeLayout';

const GenerationColumn = ({ generation, members, allMembers, groupColorMap, onRightClick, onMemberClick, onDoubleClick, onAddInColumn }) => {

  // Check if two consecutive members are a spouse pair
  const isSpousePair = (memberA, memberB) => {
    return (
      (memberA.spouseId && memberA.spouseId === memberB.memberId) ||
      (memberB.spouseId && memberB.spouseId === memberA.memberId)
    );
  };

  // Check if either member is a second spouse
  const isSecondSpousePair = (memberA, memberB) => {
    return (
      (memberA.spouse2Id && memberA.spouse2Id === memberB.memberId) ||
      (memberB.spouse2Id && memberB.spouse2Id === memberA.memberId)
    );
  };

  return (
    <div style={styles.column}>
      <div style={styles.genLabel}>
        {getGenerationLabel(generation)}
      </div>
      <div style={styles.genNumber}>
        Gen {generation > 0 ? `+${generation}` : generation}
      </div>
      <div style={styles.members}>
        {members.map((member, index) => {
          const nextMember = members[index + 1];
          const showDivider = nextMember && isSpousePair(member, nextMember);
          const isSecond = showDivider && isSecondSpousePair(member, nextMember);

          return (
            <React.Fragment key={member.memberId}>
              <div style={styles.cellWrapper}>
                {/* Left strip — outside cell, touching left border */}
                {getLeftStripColor(member, groupColorMap) && (
                  <div style={{
                    ...styles.outerStrip,
                    background: getLeftStripColor(member, groupColorMap),
                    borderRadius: '3px 0 0 3px',
                  }} />
                )}

                <MemberCell
                  member={member}
                  isCreator={!!member.isCreator}
                  onRightClick={onRightClick}
                  onClick={onMemberClick}
                  onDoubleClick={onDoubleClick}
                />

                {/* Right strip — outside cell, touching right border */}
                {getRightStripColor(member, allMembers, groupColorMap) && (
                  <div style={{
                    ...styles.outerStrip,
                    background: getRightStripColor(member, allMembers, groupColorMap),
                    borderRadius: '0 3px 3px 0',
                  }} />
                )}
              </div>

              {showDivider && (
                <div style={isSecond ? styles.dividerSecond : styles.dividerFirst} />
              )}
            </React.Fragment>
          );
        })}

        {/* Always-visible add cell — click to add a member in this generation */}
        <div
          style={styles.addCell}
          onClick={() => onAddInColumn && onAddInColumn(generation)}
          title="Add a family member here"
        >
          +
        </div>
      </div>
    </div>
  );
};

const styles = {
  column: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    minWidth: '110px',
    padding: '0 8px',
  },
  genLabel: {
    fontSize: '9px',
    color: '#9CA3AF',
    textAlign: 'center',
    marginBottom: '2px',
    whiteSpace: 'nowrap',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
  },
  genNumber: {
    fontSize: '11px',
    fontWeight: 'bold',
    color: '#D4A017',
    marginBottom: '12px',
  },
  members: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    alignItems: 'center',
  },
  // Gold dashed line — first spouse pair
  dividerFirst: {
    width: '60px',
    height: '0px',
    borderTop: '2px dashed #D4A017',
    opacity: 0.6,
    marginTop: '-6px',
    marginBottom: '-6px',
  },
  // Purple dotted line — second spouse pair
  dividerSecond: {
    width: '60px',
    height: '0px',
    borderTop: '2px dotted #7C3AED',
    opacity: 0.7,
    marginTop: '-6px',
    marginBottom: '-6px',
  },
  // Always-visible "+" cell at the bottom of every column
  addCell: {
    width: '78px',
    height: '64px',
    borderRadius: '8px',
    border: '1.5px dashed #D1D5DB',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '22px',
    lineHeight: '1',
    color: '#9CA3AF',
    cursor: 'pointer',
    background: 'transparent',
    flexShrink: 0,
    boxSizing: 'border-box',
    transition: 'all 0.15s ease',
  },
  cellWrapper: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    position: 'relative',
  },
  outerStrip: {
    width: '4px',
    flexShrink: 0,
  },
};

export default GenerationColumn;
